import { Field, Form, Formik } from "formik";
import { ScrollToFirstError } from "utils";
import {
  Button,
  CalendarInput,
  SelectComponent,
  Textarea,
  Checkbox,
  LargeSwitch,
} from "shared";
import { Input, DropFileInput } from "shared";
import { InvestmentItem } from "../../../types";
import { LoansValues } from "./types";
import { loansSchema } from "./validationSchemas";
import {
  DURATION_OPTIONS,
  INTEREST_TYPE_OPTIONS,
  INTEREST_PAYMENT_OPTIONS,
  AUTO_RENEWAL_OPTIONS,
  LOAN_TYPE_OPTIONS,
  RELIANCE_CONSENT_LABEL,
} from "./constants";

interface LoansFormProps {
  item: InvestmentItem;
  onSubmit: (values: LoansValues) => void;
  onCancel: () => void;
  isPending: boolean;
  currencyOptions: any[];
  onRemovedContract: (file: { name: string; url: string }) => void;
}

const LoansForm = ({
  item,
  onSubmit,
  onCancel,
  isPending,
  currencyOptions,
  onRemovedContract,
}: LoansFormProps) => {
  const durationDateRaw = item.details?.durationDate as
    | string
    | null
    | undefined;
  let durationDate = "";
  let durationDateValue = "";

  if (durationDateRaw === null || durationDateRaw === undefined) {
    durationDate = "indefinite";
  } else if (typeof durationDateRaw === "string" && durationDateRaw !== "") {
    const d = new Date(durationDateRaw);
    if (
      !isNaN(d.getTime()) &&
      d.getFullYear() === 2000 &&
      d.getMonth() === 0 &&
      d.getDate() === 1
    ) {
      durationDate = "";
    } else if (!isNaN(d.getTime())) {
      durationDate = "specific_date";
      durationDateValue = durationDateRaw;
    }
  }

  const autoRenewalRaw = item.details?.autoRenewal;
  const autoRenewal =
    autoRenewalRaw === true || autoRenewalRaw === 1
      ? "true"
      : autoRenewalRaw === false || autoRenewalRaw === 0
      ? "false"
      : "";

  const existingContractFiles = (item.files ?? []).filter(
    (f: any) => f.role === "contract",
  );

  const initialValues: LoansValues = {
    loanType: String(item.details?.loanType ?? "borrowed"),
    entity: String(item.details?.entity ?? ""),
    primaryValue:
      item.primaryValue !== null && item.primaryValue !== undefined
        ? String(item.primaryValue)
        : "",
    currencyUuid: item.currencyUuid ?? "",
    interestRate: String(item.details?.interestRate ?? ""),
    interestRateType: String(
      item.details?.interestRateType ?? item.details?.interestType ?? "",
    ),
    interestPayment: String(item.details?.interestPayment ?? ""),
    contractDate: item.details?.contractDate
      ? String(item.details.contractDate)
      : "",
    durationDate,
    durationDateValue,
    autoRenewal,
    additionalInfo: item.additionalInfo ?? "",
    contractFile: null,
    relianceCanView: Boolean(item.relianceCanView ?? false),
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={loansSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {(formik) => (
        <Form>
          <ScrollToFirstError />
          <div className="modal-form__fields">
            <LargeSwitch
              state={formik.values.loanType}
              options={LOAN_TYPE_OPTIONS}
              onChange={(val: string) => formik.setFieldValue("loanType", val)}
            />

            <Field
              as={Input}
              name="entity"
              label="Podmiot"
              type="text"
              error={
                (formik.touched.entity || formik.submitCount > 0) &&
                formik.errors.entity
                  ? formik.errors.entity
                  : undefined
              }
            />

            <Field
              as={Input}
              name="primaryValue"
              label="Kwota"
              type="number"
              step="any"
              precision={2}
              maxLength={9}
              error={
                (formik.touched.primaryValue || formik.submitCount > 0) &&
                formik.errors.primaryValue
                  ? formik.errors.primaryValue
                  : undefined
              }
            />

            <Field
              as={SelectComponent}
              name="currencyUuid"
              label="Waluta"
              options={currencyOptions}
              isSearchable={true}
              error={
                (formik.touched.currencyUuid || formik.submitCount > 0) &&
                formik.errors.currencyUuid
                  ? formik.errors.currencyUuid
                  : undefined
              }
            />

            <Field
              as={Input}
              name="interestRate"
              label="Wysokość oprocentowania"
              type="number"
              step="any"
              suffix="%"
              precision={2}
              maxLength={9}
              error={
                (formik.touched.interestRate || formik.submitCount > 0) &&
                formik.errors.interestRate
                  ? formik.errors.interestRate
                  : undefined
              }
            />

            <Field
              as={SelectComponent}
              name="interestRateType"
              label="Rodzaj oprocentowania"
              options={INTEREST_TYPE_OPTIONS}
              isSearchable={false}
              optional
            />

            <Field
              as={SelectComponent}
              name="interestPayment"
              label="Odsetki"
              options={INTEREST_PAYMENT_OPTIONS}
              isSearchable={false}
              optional
            />

            <Field
              type="date"
              name="contractDate"
              label="Data zawarcia"
              optional
              component={CalendarInput}
              errors={formik.errors}
              yearMonthPicker={true}
            />

            <Field
              as={SelectComponent}
              name="durationDate"
              label="Czas trwania"
              options={DURATION_OPTIONS}
              isSearchable={false}
              optional
            />

            {formik.values.durationDate === "specific_date" && (
              <Field
                type="date"
                name="durationDateValue"
                label="Data zakończenia"
                component={CalendarInput}
                errors={formik.errors}
                yearMonthPicker={true}
              />
            )}

            <Field
              as={SelectComponent}
              name="autoRenewal"
              label="Automatyczne przedłużenie"
              options={AUTO_RENEWAL_OPTIONS}
              isSearchable={false}
              optional
            />

            <Field
              component={Textarea}
              name="additionalInfo"
              label="Dodatkowe informacje"
              optional
            />

            <Field
              label="Umowa"
              name="contractFile"
              id="contractFile"
              as={DropFileInput}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              optional
              multiple={10}
              existingFiles={existingContractFiles.map((f: any) => ({
                name: f.file?.name || "Plik",
                url: f.file?.url || "",
              }))}
              onServerFileRemoved={onRemovedContract}
            />

            <Field
              as={Checkbox}
              name="relianceCanView"
              label={RELIANCE_CONSENT_LABEL}
              checked={formik.values.relianceCanView}
              onChange={() =>
                formik.setFieldValue(
                  "relianceCanView",
                  !formik.values.relianceCanView,
                )
              }
            />
          </div>

          <div className="modal-buttons">
            <Button
              type="button"
              className="button--stroke button--rounded"
              onClick={onCancel}
              label="Anuluj"
            />
            <Button
              type="submit"
              className="button--black button--rounded"
              onClick={() => {}}
              label={isPending ? "Zapisywanie..." : "Zapisz"}
              isLoading={isPending}
            />
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default LoansForm;
